import React from 'react';
import { Box, Flex, Text, Grid, Image, Fade } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { selectUser } from 'store/slices/authSlice';

import { ReactComponent as CloseIcon } from 'icons/cross.svg';
import { PuffLoader } from 'react-spinners';
import { useLocation } from 'react-router-dom';
import { formatNumberWithCommas } from 'utils/formatNumberWithCommas';
import { useGetSubscriptionsQuery } from 'store/services/api';
import { Navigate } from 'react-router';
import routes from 'constants/routes';
import { useLazyGetPaymentLinkQuery } from 'store/services/users';

import { event, EVENTS } from 'utils/analytics';
import Slider from './Slider';

const SubscriptionPage = ({ open, onClose, mode = 'default', onLinkClick }) => {
  const user = useSelector(selectUser);

  const location = useLocation();
  const { data: subscriptions, isLoading } = useGetSubscriptionsQuery();
  const [getPaymentLink, { isLoading: isGetPaymentLingLoading }] = useLazyGetPaymentLinkQuery();
  let backLink = '/';

  const getLink = async id => {
    if (isGetPaymentLingLoading) return;
    const selectedSubscription = subscriptions?.find(s => s.id === id);
    event('Purchase', EVENTS.COIN_PURCHASE, 'get_coins', {
      subscription_id: id,
      amount: selectedSubscription?.unit_amount || 0,
      coins: selectedSubscription?.coins_amount || 0,
    });

    const response = await getPaymentLink(id);
    if (response.data.url) {
      window.location.href = response.data.url;
    }
  };

  if (location.state && location.state.from) {
    backLink = location.state.from;
  }

  if (!user) {
    return <Navigate to={routes.signin} />;
  }

  return (
    <Fade
      in={open}
      unmountOnExit
      style={{
        backgroundColor: 'var(--chakra-colors-bg)',
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        overflowY: 'auto',
        padding: mode === 'default' ? '0 0 40px' : '30px 14px 40px',
        zIndex: mode === 'default' ? '201' : '1',
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
        height: mode === 'default' ? '100dvh' : '100%',
        minHeight: mode === 'default' ? '100dvh' : '100%',
        maxWidth: '400px',
        margin: '0 auto',
        overflow: 'hidden',
      }}
    >
      {mode === 'default' ? (
        <Grid
          justifyContent="space-between"
          alignItems="center"
          px="15px"
          h="48px"
          cursor="pointer"
          templateColumns="64px 1fr auto"
          templateRows="1fr"
          position="sticky"
          top="0"
          backgroundColor="var(--chakra-colors-bg)"
          zIndex="202"
        >
          <Flex
            onClick={() => {
              if (onClose) {
                const bottomSheetElem = document.createElement('div');
                bottomSheetElem.id = 'bottomSheetAfterPaywall';
                document.body.appendChild(bottomSheetElem);

                onClose();
              }
            }}
            height="100%"
            alignItems="center"
            justifyContent="flex-start"
            transition="all 0.2s ease-in-out"
            _active={{ opacity: 0.5 }}
          >
            <CloseIcon />
          </Flex>

          <Flex alignItems="center" gap="0" flexDirection="column">
            <Text
              fontSize="14px"
              lineHeight="1.2"
              fontWeight="400"
              color="var(--chakra-colors-white)"
              fontFamily="'Fugaz One', cursive"
              textTransform="uppercase"
              pt="2px"
            >
              UPGRADE TO PREMIUM
            </Text>
          </Flex>
          <Flex justifyContent="flex-end" gap="15px">
            <Flex
              gap="5px"
              backgroundColor="rgba(255, 255, 255, 0.1)"
              p="5px 9px"
              borderRadius="12px"
              alignItems="center"
              boxShadow="0px 2px 0 0 rgba(255, 255, 255, 0.06)"
              cursor="pointer"
            >
              <Image src="/images/coin.png" alt="coin" w="16px" h="16px" objectFit="contain" objectPosition="center" />
              <Box
                fontSize="14px"
                fontWeight="400"
                lineHeight="1"
                fontFamily="'Fugaz One', cursive"
                textTransform="uppercase"
                position="relative"
                top="1px"
              >
                {formatNumberWithCommas(user?.current_amount || 0)}
              </Box>
            </Flex>
          </Flex>
        </Grid>
      ) : null}
      <Flex
        flexDirection="column"
        alignItems="center"
        gap="24px"
        pos="relative"
        flex={mode === 'default' ? '1' : 'auto'}
      >
        {isLoading && (
          <Fade
            in={true}
            style={{ width: '100%', flex: '1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Flex w="100%" flex={mode === 'default' ? '1' : 'auto'} alignItems="center" justifyContent="center">
              <PuffLoader color="white" />
            </Flex>
          </Fade>
        )}

        <Fade
          in={!isLoading}
          style={{
            width: '100%',
            display: 'flex',
            flex: mode === 'default' ? '1' : 'auto',
            flexDirection: 'column',
            gap: '0px',
            padding: '0',
          }}
        >
          <Slider onButtonClick={() => getLink(subscriptions?.[0]?.id)} />
          {mode === 'default' && (
            <Flex fontSize="12px" fontWeight="600" letterSpacing="0.04em" mt="8px" w="100%" justifyContent="center">
              OR 
              <Box
                as="span"
                color="var(--chakra-colors-blue)"
                onClick={() => {
                  onLinkClick && onLinkClick();
                }}
              >
                BUY TICKET FOR COINS {'>'}
              </Box>
            </Flex>
          )}
        </Fade>
      </Flex>
    </Fade>
  );
};

export default SubscriptionPage;
